import {useQuery} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import {Group, Stack, Text, Title} from '@mantine/core';
import React from 'react';

import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import ScratchPlateDesignSummary from 'notebook/integrations/plate-maps/scratch-plates/ScratchPlateDesignSummary';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {readableTime} from 'util/DateUtil';

import {
  GetPlateDesignTemplate_Query as GetPlateDesignTemplateQuery,
  GetPlateDesignTemplateVariables,
} from './__generated/GetPlateDesignTemplate';
import {GET_PLATE_DESIGN_TEMPLATE_QUERY} from './PlateDesignTemplateQueries';

interface PlateDesignTemplatePageProps {
  plateDesignTemplateId: string;
}

function MetadataRow({label, value}: {label: string; value: string}): JSX.Element {
  return (
    <Group spacing="xs">
      <Text size="sm" color="dimmed">
        {label}:
      </Text>
      <Text size="sm">{value}</Text>
    </Group>
  );
}

export default function PlateDesignTemplatePage({
  plateDesignTemplateId,
}: PlateDesignTemplatePageProps): JSX.Element {
  const {data, loading, error} = useQuery<GetPlateDesignTemplateQuery, GetPlateDesignTemplateVariables>(
    GET_PLATE_DESIGN_TEMPLATE_QUERY,
    {variables: {id: plateDesignTemplateId}}
  );

  if (loading) {
    return <CenteredHorizontalThrobber />;
  }

  if (error || !data) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Failed to load plate design template." />;
  }

  const {plateDesignTemplate} = data;
  if (isInaccessible(plateDesignTemplate)) {
    return (
      <WorkspaceItemInaccessibleGuard
        inaccessibleInfo={plateDesignTemplate}
        modelType={ModelTypeConstants.PLATE_DESIGN_TEMPLATE}
      />
    );
  }

  const design = plateDesignTemplate;

  return (
    <Stack spacing="md" p="md">
      <Title order={3}>{design.name}</Title>
      <Group spacing="lg">
        {design.creator && <MetadataRow label="Author" value={design.creator.name} />}
        <MetadataRow label="Created" value={readableTime(design.createdAt, {showTime: false})} />
        <MetadataRow label="Modified" value={readableTime(design.modifiedAt, {showTime: false})} />
      </Group>
      <ScratchPlateDesignSummary
        scratchPlateId={design.scratchPlate.id}
        locked={true}
        viewer={true}
        onEdit={noop}
      />
    </Stack>
  );
}
